/**
 * SMESDGContribution — /sme/sdg
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Target } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, CTAButton } from "@/components/ds";

const SDG_PALETTE = {
  1: "#E5243B", 2: "#DDA63A", 3: "#4C9F38", 4: "#C5192D",
  5: "#FF3A21", 6: "#26BDE2", 7: "#FCC30B", 8: "#A21942",
  9: "#FD6925", 10: "#DD1367", 11: "#FD9D24", 12: "#BF8B2E",
  13: "#3F7E44", 14: "#0A97D9", 15: "#56C02B", 16: "#00689D", 17: "#19486A"
};

export default function SMESDGContribution() {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/sme/my-journey").then(r => setJourney(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const actions = Array.isArray(journey?.actions) ? journey.actions : [];
  const sdgMap = {
    "fleet_conversion":     [{ sdg: 13, label: "Climate Action", target: "13.2" }],
    "solar":                [{ sdg: 7, label: "Affordable and Clean Energy", target: "7.2" }, { sdg: 13, label: "Climate Action", target: "13.2" }],
    "client_fuel_switching":[{ sdg: 13, label: "Climate Action", target: "13.2" }, { sdg: 9, label: "Industry & Infrastructure", target: "9.4" }],
    "generator_diesel":     [{ sdg: 7, label: "Affordable and Clean Energy", target: "7.3" }],
  };

  const sdgTotals = {};
  actions.forEach((action) => {
    const sdgs = sdgMap[action.action_type || ""] || [{ sdg: 13, label: "Climate Action", target: "13.2" }];
    sdgs.forEach((s) => {
      if (!sdgTotals[s.sdg]) sdgTotals[s.sdg] = { ...s, reduction_tco2e: 0 };
      sdgTotals[s.sdg].reduction_tco2e += (action.co2_reduction || 0);
    });
  });

  const contributions = Object.values(sdgTotals).sort((a, b) => b.reduction_tco2e - a.reduction_tco2e);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="SME WORKSPACE"
        title="SDG Contribution"
        subtitle="Mapped from actual emissions reduction actions — not generic claims"
        backUrl="/sme/overview"
        backLabel="SME Workspace"
      />

      {contributions.length === 0 ? (
        <SectionCard>
          <div className="py-10 text-center">
            <Target size={28} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-600">No SDG contributions mapped yet</p>
            <p className="text-xs text-slate-400 mb-4">Complete your emissions assessment to unlock SDG mapping.</p>
            <CTAButton variant="action" onClick={() => navigate("/sme/monthly/inputs")}>
              Enter Monthly Data →
            </CTAButton>
          </div>
        </SectionCard>
      ) : (
        <>
          <div className="space-y-3">
            {contributions.map((c) => (
              <div key={c.sdg} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shrink-0"
                  style={{ backgroundColor: SDG_PALETTE[c.sdg] || "#999" }}
                >
                  {c.sdg}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{c.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Target {c.target}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <MetricCard label="tCO₂e/yr" value={(c.reduction_tco2e || 0).toFixed(1)} color="text-teal-700" className="min-w-[90px]" />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 italic text-center">
            SDG mapping derived from action recommendations. Rule-based, not AI-generated.
            Independent verification required for external SDG claims.
          </p>
        </>
      )}
    </div>
  );
}
